// Exact decimals for PostgreSQL numeric(p, s) columns. Values are checked as decimal text, never with
// floating-point arithmetic (0.29 * 100 is 28.999999999999996 in binary), and are never rounded: a value with more
// decimal places than its column allows is refused.

export type DecimalError = "zero_or_more" | "positive_number" | "at_least_one" | "two_decimals" | "three_decimals" | "four_decimals" | "out_of_range";

/** Precision of one kind of value: digits allowed after the point and before it, the smallest value, the largest. */
export interface DecimalSpec { scale: 2 | 3 | 4; intDigits: number; min: "zero" | "positive" | "one"; max?: string }

const SCALE_ERROR = { 2: "two_decimals", 3: "three_decimals", 4: "four_decimals" } as const;

// Value kinds used by the master data. The precision of the legacy columns is unknown until discovery reads the
// schema (checklist B3); these are Delta's choices, recorded in docs/architecture.md.
export const MONEY: DecimalSpec = { scale: 2, intDigits: 12, min: "zero" };              // numeric(14,2): amounts in SAR
export const PRICE: DecimalSpec = { scale: 4, intDigits: 14, min: "zero" };              // numeric(18,4): unit prices
export const QUANTITY: DecimalSpec = { scale: 3, intDigits: 13, min: "zero" };           // numeric(16,3): quantities (weighed goods)
export const FACTOR: DecimalSpec = { scale: 4, intDigits: 10, min: "one" };              // numeric(14,4): pieces per unit
export const PERCENT: DecimalSpec = { scale: 2, intDigits: 3, min: "zero", max: "100" }; // numeric(5,2): 0 to 100 %

export const AMOUNT_MAX = "999999999999.99";

/** Compares two non-negative canonical decimals ("12.50" style, same scale not required). */
function cmp(a: string, b: string): number {
  const [ai, af = ""] = a.split("."), [bi, bf = ""] = b.split(".");
  if (ai!.length !== bi!.length) return ai!.length < bi!.length ? -1 : 1;
  if (ai !== bi) return ai! < bi! ? -1 : 1;
  const n = Math.max(af.length, bf.length), x = af.padEnd(n, "0"), y = bf.padEnd(n, "0");
  return x === y ? 0 : x < y ? -1 : 1;
}

/** Parses a JSON number or decimal text against a spec. Returns the exact decimal text, or the error code. */
export function parseDecimal(x: unknown, spec: DecimalSpec): { value: string } | { error: DecimalError } {
  const tooSmall: DecimalError = spec.min === "one" ? "at_least_one" : spec.min === "positive" ? "positive_number" : "zero_or_more";
  let s: string;
  if (typeof x === "number") {
    if (!Number.isFinite(x) || x < 0) return { error: tooSmall };
    // String() gives the shortest text that reads back as the same number, so 0.29 stays "0.29". Very large or
    // very small numbers come out in exponent form: those are out of range or have too many decimals.
    s = String(x);
    if (/e/i.test(s)) return { error: x >= 1 ? "out_of_range" : SCALE_ERROR[spec.scale] };
  } else if (typeof x === "string") s = x.trim();
  else return { error: tooSmall };

  const m = /^(\d+)(?:\.(\d*))?$/.exec(s);
  if (!m) return { error: tooSmall };
  const int = m[1]!.replace(/^0+(?=\d)/, "");
  const frac = (m[2] ?? "").replace(/0+$/, "");
  if (int.length > spec.intDigits) return { error: "out_of_range" };
  if (frac.length > spec.scale) return { error: SCALE_ERROR[spec.scale] };
  const value = `${int}.${frac.padEnd(spec.scale, "0")}`;
  if (spec.min === "one" && cmp(value, "1") < 0) return { error: "at_least_one" };
  if (spec.min === "positive" && cmp(value, "0") === 0) return { error: "positive_number" };
  if (spec.max && cmp(value, spec.max) > 0) return { error: "out_of_range" };
  return { value };
}

/** A non-negative money amount with at most two decimals (numeric(14,2)). */
export const parseAmount = (x: unknown) => parseDecimal(x, MONEY) as { value: string } | { error: "zero_or_more" | "two_decimals" | "out_of_range" };

/** True when two canonical decimals are equal ("1.0000" and "1" are). */
export const sameDecimal = (a: string, b: string) => cmp(a, b) === 0;
